import {
  COOGO_EXECUTION_SNAPSHOT_VERSION,
  CoogoExecutionError,
  parseCoogoExecutionCheckpointV1,
  validateCoogoExecutionEvent,
  validateCoogoOrchestrationPlan,
  type CoogoExecutionCheckpointV1,
  type CoogoCheckpointObservationV1,
  type CoogoExecutionJournal,
  type CoogoExecutionSnapshot,
  type CoogoExecutionTransition,
  type CoogoExecutionTransitionReceipt,
  type CoogoReadableExecutionEvent,
} from "@cogo/webmcp-runtime";
import {
  commitStudioOrchestrationTransition,
  readStudioReadableOrchestrationJournal,
} from "./studio-orchestration-journal-api";

export type StudioCoogoExecutionRecoveryDecision =
  | {
      kind: "automatic-resume";
      gateNodeId: "n2-human-gate";
    }
  | {
      kind: "mutation_outcome_unknown";
      nodeId: "n4-update-page-meta";
      recovery: "manual-reconciliation";
    }
  | { kind: "terminal" }
  | { kind: "not-resumable" };

/**
 * Recovery is deliberately narrower than the runtime's generic resume support.
 * A process death while N4 is running has no durable mutation receipt, so even
 * an exact readback cannot authorize replay, completion synthesis, or undo.
 */
export function classifyStudioCoogoExecutionRecovery(
  checkpoint: CoogoExecutionCheckpointV1,
): StudioCoogoExecutionRecoveryDecision {
  if (checkpoint.nodes["n4-update-page-meta"]?.status === "running") {
    return {
      kind: "mutation_outcome_unknown",
      nodeId: "n4-update-page-meta",
      recovery: "manual-reconciliation",
    };
  }
  if (
    checkpoint.status === "suspended" &&
    checkpoint.nodes["n2-human-gate"]?.status === "suspended" &&
    checkpoint.nodes["n4-update-page-meta"]?.status === "pending"
  ) {
    return { kind: "automatic-resume", gateNodeId: "n2-human-gate" };
  }
  if (
    checkpoint.status === "completed" ||
    checkpoint.status === "failed" ||
    checkpoint.status === "cancelled"
  ) {
    return { kind: "terminal" };
  }
  return { kind: "not-resumable" };
}

export interface StudioCoogoUnknownMutationOutcome {
  kind: "mutation_outcome_unknown";
  nodeId: "n4-update-page-meta";
  observedOutcome: "exact-before" | "exact-proposed" | "diverged";
  beforeValue: string;
  proposedValue: string;
  recovery: "manual-reconciliation";
}

/** Records readback evidence but deliberately never converts it into replay authority. */
export function classifyStudioCoogoUnknownMutationOutcome(input: {
  checkpoint: CoogoExecutionCheckpointV1;
  proposedValue: string;
  observation: CoogoCheckpointObservationV1;
}): StudioCoogoUnknownMutationOutcome {
  if (input.checkpoint.nodes["n4-update-page-meta"]?.status !== "running") {
    throw new CoogoExecutionError(
      "Unknown mutation outcome classification requires recovered running N4.",
      "mutation_outcome_not_unknown",
    );
  }
  const beforeOutput =
    input.checkpoint.nodes["n1-impact-before"]?.checkpointOutput;
  const beforeObservation =
    beforeOutput?.kind === "exact-impact"
      ? beforeOutput.observations.find(
          (item) => item.path === input.observation.path,
        )
      : undefined;
  if (
    !beforeObservation ||
    beforeObservation.presence !== "present" ||
    beforeObservation.valueKind !== "json-primitive" ||
    typeof beforeObservation.value !== "string"
  ) {
    throw new CoogoExecutionError(
      "Recovered N4 requires the durable N1 exact-impact value.",
      "mutation_outcome_evidence_missing",
    );
  }
  const observedValue =
    input.observation.presence === "present" &&
    input.observation.valueKind === "json-primitive" &&
    typeof input.observation.value === "string"
      ? input.observation.value
      : undefined;
  const observedOutcome =
    observedValue === beforeObservation.value
      ? "exact-before"
      : observedValue === input.proposedValue
        ? "exact-proposed"
        : "diverged";
  return {
    kind: "mutation_outcome_unknown",
    nodeId: "n4-update-page-meta",
    observedOutcome,
    beforeValue: beforeObservation.value,
    proposedValue: input.proposedValue,
    recovery: "manual-reconciliation",
  };
}

function snapshotFromDurableCheckpoint(
  checkpoint: CoogoExecutionCheckpointV1,
  events: CoogoReadableExecutionEvent[],
): CoogoExecutionSnapshot {
  return {
    schemaVersion: COOGO_EXECUTION_SNAPSHOT_VERSION,
    planId: checkpoint.planId,
    runId: checkpoint.runId,
    projectId: checkpoint.projectId,
    planFingerprint: checkpoint.planFingerprint,
    outputNodeIds: structuredClone(checkpoint.outputNodeIds),
    ...(checkpoint.admissionBinding
      ? { admissionBinding: structuredClone(checkpoint.admissionBinding) }
      : {}),
    status: checkpoint.status,
    sequence: checkpoint.sequence,
    nodes: Object.fromEntries(
      Object.entries(checkpoint.nodes).map(([nodeId, node]) => [
        nodeId,
        {
          nodeId,
          status: node.status,
          ...(node.error ? { error: structuredClone(node.error) } : {}),
          ...(node.suspension
            ? {
                suspension: {
                  reason: node.suspension.reason,
                  data: {
                    ...structuredClone(node.suspension),
                    target: structuredClone(node.suspension.target),
                  },
                },
              }
            : {}),
          ...(node.telemetry
            ? { telemetry: structuredClone(node.telemetry) }
            : {}),
          ...(node.checkpointOutput
            ? { output: structuredClone(node.checkpointOutput) }
            : {}),
        },
      ]),
    ),
    events: structuredClone(events),
    updatedAt: checkpoint.updatedAt,
  };
}

export interface StudioCoogoExecutionJournal extends CoogoExecutionJournal {
  readonly checkpointRevision: number;
}

function inconsistentJournal(message: string): CoogoExecutionError {
  return new CoogoExecutionError(
    `Durable orchestration readback is inconsistent: ${message}`,
    "journal_checkpoint_inconsistent",
  );
}

export function createStudioCoogoExecutionJournal(input: {
  projectId: string;
  runId: string;
  onLeaseLost?: (error: CoogoExecutionError) => void;
}): StudioCoogoExecutionJournal {
  let checkpointRevision = 0;
  return {
    get checkpointRevision() {
      return checkpointRevision;
    },
    async load(runId: string) {
      if (runId !== input.runId) {
        throw new CoogoExecutionError(
          "Journal run identity does not match the configured durable run.",
          "journal_run_mismatch",
        );
      }
      const readback = await readStudioReadableOrchestrationJournal({
        projectId: input.projectId,
        runId,
      });
      if (!readback) {
        throw new CoogoExecutionError(
          "Authenticated durable journal readback is required.",
          "durable_orchestration_auth_required",
        );
      }
      if (
        readback.run.runId !== input.runId ||
        readback.run.projectId !== input.projectId ||
        readback.plan.runId !== input.runId ||
        readback.plan.projectId !== input.projectId ||
        readback.plan.planId !== readback.run.planId
      ) {
        throw inconsistentJournal("run or plan identity does not match");
      }
      const planValidation = validateCoogoOrchestrationPlan(readback.plan);
      if (!planValidation.valid) {
        throw new CoogoExecutionError(
          `Invalid durable orchestration plan: ${planValidation.errors.join("; ")}`,
          "invalid_durable_plan",
        );
      }
      if (
        !Number.isInteger(readback.run.checkpointRevision) ||
        (readback.run.checkpointRevision as number) < 0 ||
        !Number.isInteger(readback.run.lastSequence) ||
        readback.run.lastSequence < -1 ||
        readback.events.length !== readback.run.lastSequence + 1
      ) {
        throw inconsistentJournal(
          "revision, sequence, or event count is invalid",
        );
      }
      for (const [index, event] of readback.events.entries()) {
        const validation = validateCoogoExecutionEvent(event);
        if (!validation.valid) {
          throw new CoogoExecutionError(
            `Invalid durable execution event: ${validation.errors.join("; ")}`,
            "invalid_durable_event",
          );
        }
        if (
          event.sequence !== index ||
          event.planId !== readback.run.planId ||
          event.runId !== input.runId ||
          event.projectId !== input.projectId
        ) {
          throw inconsistentJournal(
            "event history identity or sequence is invalid",
          );
        }
      }
      checkpointRevision = readback.run.checkpointRevision as number;
      if (readback.checkpoint === null || readback.checkpoint === undefined) {
        if (
          checkpointRevision !== 0 ||
          readback.run.lastSequence !== -1 ||
          readback.events.length !== 0
        ) {
          throw inconsistentJournal(
            "only a pristine run may omit its checkpoint",
          );
        }
        return undefined;
      }
      const checkpoint = parseCoogoExecutionCheckpointV1(readback.checkpoint);
      const v2EventCount = readback.events.filter(
        (event) => event.schemaVersion === "coogo.execution-event.v2",
      ).length;
      if (
        checkpointRevision <= 0 ||
        checkpointRevision !== v2EventCount ||
        checkpoint.runId !== input.runId ||
        checkpoint.projectId !== input.projectId ||
        checkpoint.planId !== readback.run.planId ||
        checkpoint.sequence !== readback.run.lastSequence ||
        checkpoint.status !== readback.run.status
      ) {
        throw inconsistentJournal(
          "checkpoint identity, revision, sequence, or status is invalid",
        );
      }
      return {
        checkpointRevision,
        snapshot: snapshotFromDurableCheckpoint(checkpoint, readback.events),
      };
    },
    async commitTransition(
      transition: CoogoExecutionTransition,
    ): Promise<CoogoExecutionTransitionReceipt> {
      if (
        transition.event.runId !== input.runId ||
        transition.event.projectId !== input.projectId
      ) {
        throw new CoogoExecutionError(
          "Transition identity does not match the configured durable run.",
          "journal_run_mismatch",
        );
      }
      try {
        const receipt = await commitStudioOrchestrationTransition(transition);
        checkpointRevision = receipt.checkpointRevision;
        return receipt;
      } catch (cause) {
        if (
          cause instanceof CoogoExecutionError &&
          new Set([
            "execution_lease_lost",
            "lease_fence_stale",
            "lease_fence_conflict",
          ]).has(cause.code)
        ) {
          const error =
            cause.code === "execution_lease_lost"
              ? cause
              : new CoogoExecutionError(cause.message, "execution_lease_lost");
          input.onLeaseLost?.(error);
          throw error;
        }
        throw cause;
      }
    },
  };
}
